import { useState, useEffect, useCallback, useRef } from 'react';
import { DriveAPI } from '../core/DriveAPI.js';

export function useSync(authUser, acquireDriveToken, applyData, getAllData, isRunningAsPWA) {
    // ── Sync State ──────────────────────────────────────────
    const [syncStatus, setSyncStatus] = useState('idle');
    const [lastSynced, setLastSynced] = useState('');
    const [driveFileId, setDriveFileId] = useState(null);
    const [syncStuck, setSyncStuck] = useState(false);
    const [syncErrorCount, setSyncErrorCount] = useState(0);
    const [driveBackups, setDriveBackups] = useState([]);
    const [backupStatus, setBackupStatus] = useState('idle');
    const [driveBackupsLoading, setDriveBackupsLoading] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    // ── Online/Offline Event Listeners ────────────────────────
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Refs
    const syncDebounceRef = useRef(null);
    const syncStuckTimerRef = useRef(null);
    const isSyncingRef = useRef(false);
    const tokenRefreshRef = useRef(null);
    const lastPushedAtRef = useRef(null);
    const localDirtyRef = useRef(false);
    const pollIntervalRef = useRef(null);
    const pullDebounceRef = useRef(null);
    const lastPullRef = useRef(0);
    const isFirstLoad = useRef(true);
    const hasSyncedThisSessionRef = useRef(false);
    const visibilityPollRef = useRef(null);

    const PULL_COOLDOWN = 10_000;
    const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

    // ── Merge Logic ─────────────────────────────────────────
    const mergeData = useCallback((local, drive) => {
        const localTs = local._syncedAt ? new Date(local._syncedAt) : new Date(0);
        const driveTs = drive._syncedAt ? new Date(drive._syncedAt) : new Date(0);
        const driveIsNewer = driveTs > localTs;

        const keyOf = (item) => {
            if (item && item.id != null && item.id !== '') return `id:${item.id}`;
            const parts = [
                item.name || item.accountName || item.cardName || item.ticker || '',
                item.owner || '',
                item.type || '',
                item.quality || '',
                item.weight != null ? String(item.weight) : '',
                item.bank || '',
            ].filter(Boolean).join('|');
            return `k:${parts || JSON.stringify(item).slice(0, 80)}`;
        };

        const mergeArrayById = (localArr, driveArr) => {
            const map = new Map();
            (localArr || []).forEach(item => map.set(keyOf(item), item));
            (driveArr || []).forEach(item => {
                const k = keyOf(item);
                if (driveIsNewer || !map.has(k)) {
                    map.set(k, item);
                }
            });
            return Array.from(map.values());
        };

        const mergeBudgetItems = (localArr, driveArr) => {
            const map = new Map();
            (localArr || []).forEach(item => map.set(item.id, { ...item }));
            (driveArr || []).forEach(item => {
                const existing = map.get(item.id);
                if (!existing) {
                    map.set(item.id, item);
                } else if (driveIsNewer) {
                    const localLogs = existing.spendLog || [];
                    const driveLogs = item.spendLog || [];
                    const logSet = new Set(localLogs.map(l => `${l.date}|${l.amount}|${l.note || ''}`));
                    const mergedLogs = [...localLogs];
                    driveLogs.forEach(l => {
                        const key = `${l.date}|${l.amount}|${l.note || ''}`;
                        if (!logSet.has(key)) mergedLogs.push(l);
                    });
                    const mergedActual = mergedLogs.reduce((sum, l) => sum + (l.amount || 0), 0);
                    map.set(item.id, {
                        ...existing,
                        ...item,
                        spendLog: mergedLogs,
                        actualSpent: mergedActual,
                    });
                }
            });
            return Array.from(map.values());
        };

        const scalarSource = driveIsNewer ? drive : local;
        const now = new Date().toISOString();

        return {
            goldInventory: mergeArrayById(local.goldInventory, drive.goldInventory),
            personalLoans: mergeArrayById(local.personalLoans, drive.personalLoans),
            goldLoans: mergeArrayById(local.goldLoans, drive.goldLoans),
            accounts: mergeArrayById(local.accounts, drive.accounts),
            creditCards: mergeArrayById(local.creditCards, drive.creditCards),
            investments: mergeArrayById(local.investments, drive.investments),
            moneyMarketFunds: mergeArrayById(local.moneyMarketFunds, drive.moneyMarketFunds),
            fixedAssets: mergeArrayById(local.fixedAssets, drive.fixedAssets),
            monthlyIncomes: mergeArrayById(local.monthlyIncomes, drive.monthlyIncomes),
            financialMilestones: mergeArrayById(local.financialMilestones, drive.financialMilestones),
            sgBudgetCycle1: mergeBudgetItems(local.sgBudgetCycle1, drive.sgBudgetCycle1),
            sgBudgetCycle2: mergeBudgetItems(local.sgBudgetCycle2, drive.sgBudgetCycle2),
            lkBudgetDamith: mergeBudgetItems(local.lkBudgetDamith, drive.lkBudgetDamith),
            lkBudgetShan: mergeBudgetItems(local.lkBudgetShan, drive.lkBudgetShan),
            sgdRate: scalarSource.sgdRate,
            lkrRate: scalarSource.lkrRate,
            usdRate: scalarSource.usdRate,
            baseCurrency: scalarSource.baseCurrency || 'SGD',
            invCashSGD: scalarSource.invCashSGD || 0,
            invCashUSD: scalarSource.invCashUSD || 0,
            cycleHistory: (() => {
                const map = new Map();
                [...(local.cycleHistory || []), ...(drive.cycleHistory || [])]
                    .forEach(c => { if (!map.has(c.cycleId)) map.set(c.cycleId, c); });
                return Array.from(map.values()).slice(0, 24);
            })(),
            cycleOpenedAt: (() => {
                const l = local.cycleOpenedAt || '';
                const d = drive.cycleOpenedAt || '';
                return l > d ? l : d;
            })(),
            monthlySnapshots: (() => {
                const map = new Map();
                [...(local.monthlySnapshots || []), ...(drive.monthlySnapshots || [])]
                    .forEach(s => { if (!map.has(s.period)) map.set(s.period, s); });
                return Array.from(map.values()).slice(0, 24);
            })(),
            _syncedAt: now,
            version: drive.version || local.version || '3.0',
        };
    }, []);

    // ── Push to Drive ───────────────────────────────────────
    const syncToDrive = useCallback(async (forceData, isRetry = false) => {
        if (!authUser) return;
        if (isSyncingRef.current) {
            if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
            syncDebounceRef.current = setTimeout(() => syncToDrive(forceData, isRetry), 2000);
            return;
        }

        isSyncingRef.current = true;
        setSyncStatus('syncing');

        try {
            const token = await acquireDriveToken();
            const rawData = forceData || getAllData();
            const data = { ...rawData, _syncedAt: new Date().toISOString() };
            let fid = driveFileId;
            if (!fid) {
                const existing = await DriveAPI.findFile(token);
                fid = existing ? existing.id : null;
                setDriveFileId(fid);
            }
            const result = await DriveAPI.writeFile(token, fid, data);
            if (!fid && result.id) setDriveFileId(result.id);
            const existingRaw = localStorage.getItem('iPlannerData_v3');
            if (existingRaw) {
                const existingParsed = JSON.parse(existingRaw);
                localStorage.setItem('iPlannerData_v3', JSON.stringify({ ...existingParsed, _syncedAt: data._syncedAt }));
            }
            setLastSynced(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            setSyncStatus('synced');
            setSyncErrorCount(0);
            lastPushedAtRef.current = data._syncedAt;
            localDirtyRef.current = false;
            hasSyncedThisSessionRef.current = true;
        } catch (err) {
            console.error('[iPlanner] Drive sync error:', err);
            setSyncStatus('error');
            const nextRetryCount = isRetry ? syncErrorCount + 1 : 1;
            setSyncErrorCount(nextRetryCount);
            if (nextRetryCount <= 3) {
                const delay = Math.pow(2, nextRetryCount) * 1000;
                console.log(`[iPlanner] Retrying sync in ${delay}ms (attempt ${nextRetryCount})`);
                setTimeout(() => syncToDrive(forceData, true), delay);
            }
        } finally {
            isSyncingRef.current = false;
        }
    }, [authUser, driveFileId, getAllData, acquireDriveToken, syncErrorCount]);

    // ── Auto-sync trigger ─────────────────────────────────────
    const markDirtyAndSync = useCallback(() => {
        if (!hasSyncedThisSessionRef.current || isSyncingRef.current) return;
        localDirtyRef.current = true;
        if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
        syncDebounceRef.current = setTimeout(() => {
            const raw = localStorage.getItem('iPlannerData_v3');
            const freshData = raw ? JSON.parse(raw) : null;
            syncToDrive(freshData || undefined);
        }, 3000);
    }, [syncToDrive]);

    // ── Pull from Drive ─────────────────────────────────────
    const pullFromDrive = useCallback(async (forceInteractive = false) => {
        if (!authUser || isSyncingRef.current) return;

        isSyncingRef.current = true;
        setSyncStatus(forceInteractive ? 'syncing' : (!window.__gAccessToken ? 'connecting' : 'syncing'));

        try {
            const token = await acquireDriveToken(forceInteractive);
            let fid = driveFileId;
            if (!fid) {
                const existing = await DriveAPI.findFile(token);
                if (!existing) {
                    const local = localStorage.getItem('iPlannerData_v3');
                    if (local) {
                        const localData = JSON.parse(local);
                        const pushData = { ...localData, _syncedAt: new Date().toISOString() };
                        const result = await DriveAPI.writeFile(token, null, pushData);
                        if (result.id) setDriveFileId(result.id);
                        lastPushedAtRef.current = pushData._syncedAt;
                    }
                    localDirtyRef.current = false;
                    hasSyncedThisSessionRef.current = true;
                    setLastSynced(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
                    setSyncStatus('synced');
                    isFirstLoad.current = false;
                    return;
                }
                fid = existing.id;
                setDriveFileId(fid);
            }

            const driveData = await DriveAPI.readFile(token, fid);
            const driveTs = driveData._syncedAt ? new Date(driveData._syncedAt) : new Date(0);

            const raw = localStorage.getItem('iPlannerData_v3');
            const localData = raw ? JSON.parse(raw) : null;

            if (!localData) {
                console.log('[iPlanner] No local data — pulling from Drive');
                localStorage.setItem('iPlannerData_v3', JSON.stringify(driveData));
                applyData(driveData);
                lastPushedAtRef.current = driveData._syncedAt;
                localDirtyRef.current = false;
            } else {
                const localTs = localData._syncedAt ? new Date(localData._syncedAt) : new Date(0);
                const alreadyInSync = localTs.getTime() === driveTs.getTime();

                if (alreadyInSync && !localDirtyRef.current) {
                    console.log('[iPlanner] Sync: already in sync');
                    // On first session load, still apply localStorage data to React state.
                    // This defensive call ensures React state is correct even if effect
                    // ordering caused useLocalStoragePersistence to fire before loadData.
                    if (!hasSyncedThisSessionRef.current) {
                        applyData(localData);
                    }
                } else if (!localDirtyRef.current) {
                    console.log('[iPlanner] Clean pull — Drive is authoritative');
                    localStorage.setItem('iPlannerData_v3', JSON.stringify(driveData));
                    applyData(driveData);
                    lastPushedAtRef.current = driveData._syncedAt;
                    localDirtyRef.current = false;
                } else {
                    console.log('[iPlanner] Conflict merge — merging');
                    const merged = mergeData(localData, driveData);
                    localStorage.setItem('iPlannerData_v3', JSON.stringify(merged));
                    applyData(merged);
                    await DriveAPI.writeFile(token, fid, merged);
                    lastPushedAtRef.current = merged._syncedAt;
                    localDirtyRef.current = false;
                }
            }

            hasSyncedThisSessionRef.current = true;
            setLastSynced(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            setSyncStatus('synced');
        } catch (err) {
            console.error('[iPlanner] Sync failed:', err);
            if (!forceInteractive && isFirstLoad.current && isRunningAsPWA &&
                (err.message?.includes('popup') || err.message?.includes('blocked') ||
                    err.message?.includes('no token') || err.message?.includes('GIS') ||
                    err.message?.includes('access_denied') || err.message?.includes('disallowed_useragent') ||
                    err.message?.includes('timeout'))) {
                console.warn('[iPlanner] PWA silent sync failed. Showing Reconnect button.');
                setSyncStatus('error');
                setSyncStuck(true);
            } else {
                setSyncStatus('error');
            }
        } finally {
            isSyncingRef.current = false;
            isFirstLoad.current = false;
        }
    }, [authUser, driveFileId, acquireDriveToken, applyData, mergeData, isRunningAsPWA]);

    // ── Backup ──────────────────────────────────────────────
    const backupToDrive = useCallback(async () => {
        if (!authUser) return;
        setBackupStatus('backing-up');
        try {
            const token = await acquireDriveToken();
            const data = getAllData();
            const newFile = await DriveAPI.writeBackupFile(token, data);
            setDriveBackups(prev => [newFile, ...prev]);
            setBackupStatus('done');
            setTimeout(() => setBackupStatus('idle'), 3000);
        } catch (err) {
            console.error('[iPlanner] Drive backup error:', err);
            setBackupStatus('error');
            setTimeout(() => setBackupStatus('idle'), 4000);
        }
    }, [authUser, getAllData, acquireDriveToken]);

    // ── Force Reconnect ─────────────────────────────────────
    const handleForceReconnect = useCallback(() => {
        console.log('[iPlanner] Force reconnect triggered');
        setSyncStuck(false);
        window.__gAccessToken = null;
        window.__gTokenExpiry = null;
        if (authUser) {
            pullFromDrive(true);
        }
    }, [authUser, pullFromDrive]);

    // ── Effects ─────────────────────────────────────────────
    // Auto-pull on auth restore / login.
    // GIS script loads async+defer so window.google may not be ready when authUser
    // is restored from localStorage. Poll with backoff until GIS is available, then pull.
    useEffect(() => {
        if (!authUser) return;
        if (hasSyncedThisSessionRef.current) return;

        let attempts = 0;
        const MAX_ATTEMPTS = 20; // up to ~10s total
        const RETRY_MS = 500;
        let timer = null;

        const tryPull = () => {
            if (hasSyncedThisSessionRef.current) return; // synced by another path, abort
            if (!window.google || !window.google.accounts) {
                attempts++;
                if (attempts < MAX_ATTEMPTS) {
                    timer = setTimeout(tryPull, RETRY_MS);
                } else {
                    console.warn('[iPlanner] GIS not ready after retries — skipping auto-pull');
                }
                return;
            }
            const now = Date.now();
            if (now - lastPullRef.current < PULL_COOLDOWN) return;
            lastPullRef.current = now;
            console.log('[iPlanner] Auth restored — triggering silent pull');
            pullFromDrive(false);
        };

        timer = setTimeout(tryPull, 200); // small initial delay to let GIS bootstrap
        return () => { if (timer) clearTimeout(timer); };
    }, [authUser, pullFromDrive]);

    // Visibility change — re-pull when tab regains focus
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState !== 'visible') return;
            if (!authUser || !hasSyncedThisSessionRef.current) return;
            // Don't pull if local changes are pending a push — let the push win
            if (localDirtyRef.current || isSyncingRef.current) return;
            const now = Date.now();
            if (now - lastPullRef.current < PULL_COOLDOWN) return;
            lastPullRef.current = now;
            console.log('[iPlanner] Tab visible — triggering background pull');
            pullFromDrive(false);
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [authUser, pullFromDrive]);

    // Periodic 5-min poll — read-only, skips if dirty or tab hidden
    useEffect(() => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (!authUser) return;
        pollIntervalRef.current = setInterval(() => {
            if (!hasSyncedThisSessionRef.current) return;
            if (localDirtyRef.current || isSyncingRef.current) return;
            if (document.visibilityState !== 'visible') return;
            const now = Date.now();
            if (now - lastPullRef.current < PULL_COOLDOWN) return;
            lastPullRef.current = now;
            console.log('[iPlanner] Periodic poll — pulling from Drive');
            pullFromDrive(false);
        }, POLL_INTERVAL);
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, [authUser, pullFromDrive]);

    // Online resume
    useEffect(() => {
        const handleOnline = () => {
            if (authUser && navigator.onLine) {
                setTimeout(() => {
                    if (Date.now() - lastPullRef.current >= PULL_COOLDOWN) {
                        lastPullRef.current = Date.now();
                        pullFromDrive();
                    }
                }, 1000);
            }
        };
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [authUser, pullFromDrive]);

    // Stuck detection
    useEffect(() => {
        if (syncStuckTimerRef.current) {
            clearTimeout(syncStuckTimerRef.current);
            syncStuckTimerRef.current = null;
        }
        if (syncStatus === 'syncing' || syncStatus === 'pulling') {
            syncStuckTimerRef.current = setTimeout(() => {
                console.warn('[iPlanner] Sync appears stuck (>15s)');
                setSyncStuck(true);
            }, 15000);
        } else {
            setSyncStuck(false);
        }
        return () => {
            if (syncStuckTimerRef.current) clearTimeout(syncStuckTimerRef.current);
        };
    }, [syncStatus]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
            if (syncStuckTimerRef.current) clearTimeout(syncStuckTimerRef.current);
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (pullDebounceRef.current) clearTimeout(pullDebounceRef.current);
            if (tokenRefreshRef.current) clearInterval(tokenRefreshRef.current);
            if (visibilityPollRef.current) clearTimeout(visibilityPollRef.current);
        };
    }, []);

    return {
        syncStatus,
        lastSynced,
        driveFileId,
        syncStuck,
        syncErrorCount,
        driveBackups,
        backupStatus,
        driveBackupsLoading,
        isOnline,
        localDirtyRef,
        hasSyncedThisSessionRef,
        setDriveBackups,
        setDriveBackupsLoading,
        setSyncStatus,
        syncToDrive,
        pullFromDrive,
        backupToDrive,
        handleForceReconnect,
        markDirtyAndSync,
    };
}